/**
 * The petal scatter that decorates a brand surface.
 *
 * It lives here rather than in the sign-in screen that first needed it: the
 * sidebar uses the same treatment, and two copies of a hand-placed array would
 * drift apart the first time one of them was adjusted.
 */
import type { CSSProperties } from 'react';

/*
 * Petals strewn across a brand surface.
 *
 * Hand-placed rather than randomised: a random scatter clusters and leaves bald
 * patches, and it would differ on every render. The larger ones are the most
 * dissolved, so size does not read as importance.
 *
 * Two arrangements, because the surfaces are different shapes. A tall panel and
 * a wide banner cannot share placements without the banner cropping most of
 * them away.
 */
const PETALS_PANEL = [
  { top: '11%', left: '19%', size: '124px', rotate: '-18deg', opacity: 0.13, blur: '2px' },
  { top: '27%', left: '76%', size: '68px', rotate: '24deg', opacity: 0.16, blur: '1px' },
  { top: '52%', left: '11%', size: '84px', rotate: '9deg', opacity: 0.14, blur: '1.4px' },
  { top: '71%', left: '82%', size: '150px', rotate: '-32deg', opacity: 0.1, blur: '3px' },
  { top: '88%', left: '38%', size: '96px', rotate: '15deg', opacity: 0.12, blur: '2px' },
  { top: '4%', left: '58%', size: '58px', rotate: '-8deg', opacity: 0.15, blur: '1px' },
  { top: '62%', left: '58%', size: '54px', rotate: '38deg', opacity: 0.1, blur: '1.6px' },
];

const PETALS_BANNER = [
  { top: '18%', left: '68%', size: '120px', rotate: '-16deg', opacity: 0.13, blur: '2px' },
  { top: '78%', left: '84%', size: '86px', rotate: '27deg', opacity: 0.11, blur: '1.6px' },
  { top: '-6%', left: '90%', size: '150px', rotate: '-34deg', opacity: 0.09, blur: '3px' },
  { top: '84%', left: '58%', size: '58px', rotate: '12deg', opacity: 0.14, blur: '1px' },
  { top: '30%', left: '96%', size: '70px', rotate: '-6deg', opacity: 0.12, blur: '1.4px' },
];

/**
 * Gathered into one corner rather than strewn about, for a surface whose middle
 * is full of text. Placed past the edge so they read as cropped by the panel.
 */
const PETALS_CORNER = [
  { top: '84%', left: '86%', size: '150px', rotate: '-24deg', opacity: 0.16, blur: '2px' },
  { top: '96%', left: '54%', size: '96px', rotate: '14deg', opacity: 0.12, blur: '2.4px' },
  { top: '70%', left: '108%', size: '120px', rotate: '32deg', opacity: 0.1, blur: '3px' },
];

const LAYOUTS = { panel: PETALS_PANEL, banner: PETALS_BANNER, corner: PETALS_CORNER };

export function Petals({ layout = 'panel' }: { layout?: keyof typeof LAYOUTS }) {
  const petals = LAYOUTS[layout];
  return (
    <>
      {petals.map((petal) => (
        <img
          // Position is the identity here: these never reorder, and nothing about
          // a petal distinguishes it except where it sits.
          key={`${petal.top}-${petal.left}`}
          className="petal"
          src="/flower.svg"
          alt=""
          aria-hidden
          style={
            {
              '--petal-top': petal.top,
              '--petal-left': petal.left,
              '--petal-size': petal.size,
              '--petal-rotate': petal.rotate,
              '--petal-opacity': petal.opacity,
              '--petal-blur': petal.blur,
            } as CSSProperties
          }
        />
      ))}
    </>
  );
}
